import * as XLSX from 'xlsx';

/**
 * XLSX parsing utility for D-Tech files using SheetJS
 * Converts XLSX files to string-based format compatible with CSV processing pipeline
 */
export class DTechXLSXHandler {
  
  /**
   * Parse XLSX file buffer and convert to CSV-compatible string format
   * @param buffer ArrayBuffer containing XLSX file data
   * @param filename Original filename for error reporting
   * @returns String representation compatible with CSV parser
   */
  static parseXLSXToCSV(buffer: ArrayBuffer, filename: string = ''): string {
    try {
      // Parse the XLSX file buffer
      const workbook = XLSX.read(buffer, { 
        type: 'array',
        cellText: true,    // Keep everything as text (avoid auto-conversion)
        cellDates: false,  // Don't auto-convert dates
        raw: false         // Return formatted strings, not raw values
      });

      // Get the first worksheet (D-Tech files typically have one sheet)
      const sheetNames = workbook.SheetNames;
      if (sheetNames.length === 0) {
        throw new Error('No worksheets found in XLSX file');
      }

      const firstSheetName = sheetNames[0];
      const worksheet = workbook.Sheets[firstSheetName];

      if (!worksheet) {
        throw new Error(`Worksheet "${firstSheetName}" not found`);
      }

      // Convert worksheet to CSV format with string preservation
      const csvData = XLSX.utils.sheet_to_csv(worksheet);

      if (!csvData || csvData.trim().length === 0) {
        throw new Error('No data found in XLSX worksheet');
      }

      return csvData;

    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      throw new Error(`Failed to parse XLSX file ${filename}: ${errorMessage}`);
    }
  }

  /**
   * Extract headers from XLSX file for file type detection
   * @param buffer ArrayBuffer containing XLSX file data
   * @returns Array of header column names
   */
  static extractHeaders(buffer: ArrayBuffer): string[] {
    try {
      const workbook = XLSX.read(buffer, { 
        type: 'array',
        cellText: true,
        raw: false
      });

      const firstSheetName = workbook.SheetNames[0];
      if (!firstSheetName) {
        return [];
      }

      const worksheet = workbook.Sheets[firstSheetName];
      if (!worksheet) {
        return [];
      }

      // Get the range of the worksheet
      const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1');
      const headers: string[] = [];

      // Extract first row as headers
      for (let col = range.s.c; col <= range.e.c; col++) {
        const cellRef = XLSX.utils.encode_cell({ r: 0, c: col });
        const cell = worksheet[cellRef];
        
        if (cell && cell.v !== undefined) {
          headers.push(String(cell.v).trim());
        } else {
          headers.push(''); // Empty header
        }
      }

      return headers.filter(h => h.length > 0); // Remove empty headers

    } catch (error) {
      console.warn('Failed to extract headers from XLSX:', error);
      return [];
    }
  }

  /**
   * Validate that XLSX file has expected D-Tech structure
   * @param buffer ArrayBuffer containing XLSX file data
   * @returns Validation result with details
   */
  static validateDTechXLSX(buffer: ArrayBuffer): {
    isValid: boolean;
    hasHeaders: boolean;
    hasData: boolean;
    rowCount: number;
    columnCount: number;
    errors: string[];
  } {
    const errors: string[] = [];
    let isValid = false;
    let hasHeaders = false;
    let hasData = false;
    let rowCount = 0;
    let columnCount = 0;

    try {
      const workbook = XLSX.read(buffer, { type: 'array' });
      
      if (workbook.SheetNames.length === 0) {
        errors.push('No worksheets found');
        return { isValid, hasHeaders, hasData, rowCount, columnCount, errors };
      }

      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      if (!worksheet['!ref']) {
        errors.push('Worksheet is empty');
        return { isValid, hasHeaders, hasData, rowCount, columnCount, errors };
      }

      const range = XLSX.utils.decode_range(worksheet['!ref']);
      rowCount = range.e.r + 1; // 0-indexed, so add 1
      columnCount = range.e.c + 1;

      // Check for minimum structure
      if (rowCount < 2) {
        errors.push('File must have at least 2 rows (headers + data)');
      } else {
        hasData = true;
      }

      if (columnCount < 3) {
        errors.push('File must have at least 3 columns');
      }

      // Check for headers in first row
      const headers = this.extractHeaders(buffer);
      if (headers.length > 0) {
        hasHeaders = true;
      } else {
        errors.push('No valid headers found in first row');
      }

      // Basic D-Tech file validation
      const dtechKeywords = ['date', 'time', 'sample', 'speed', 'angle'];
      const headerText = headers.join(' ').toLowerCase();
      const hasKeywords = dtechKeywords.some(keyword => headerText.includes(keyword));
      
      if (!hasKeywords) {
        errors.push('Headers do not contain typical D-Tech column names');
      }

      isValid = errors.length === 0 && hasHeaders && hasData;

    } catch (error) {
      errors.push(`XLSX parsing error: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }

    return {
      isValid,
      hasHeaders,
      hasData,
      rowCount,
      columnCount,
      errors
    };
  }

  /**
   * Get basic information about XLSX file without full parsing
   * @param buffer ArrayBuffer containing XLSX file data
   * @returns File information summary
   */
  static getFileInfo(buffer: ArrayBuffer): {
    sheetCount: number;
    sheetNames: string[];
    firstSheetRows: number;
    firstSheetCols: number;
    estimatedSize: string;
  } {
    try {
      const workbook = XLSX.read(buffer, { type: 'array', bookSheets: true });
      
      const sheetNames = workbook.SheetNames;
      const sheetCount = sheetNames.length;
      
      let firstSheetRows = 0;
      let firstSheetCols = 0;
      
      if (sheetCount > 0) {
        const firstSheet = workbook.Sheets[sheetNames[0]];
        if (firstSheet && firstSheet['!ref']) {
          const range = XLSX.utils.decode_range(firstSheet['!ref']);
          firstSheetRows = range.e.r + 1;
          firstSheetCols = range.e.c + 1;
        }
      }

      // Estimate size category
      const totalCells = firstSheetRows * firstSheetCols;
      let estimatedSize = 'Small';
      if (totalCells > 50000) estimatedSize = 'Large';
      else if (totalCells > 10000) estimatedSize = 'Medium';

      return {
        sheetCount,
        sheetNames,
        firstSheetRows,
        firstSheetCols,
        estimatedSize
      };

    } catch (error) {
      return {
        sheetCount: 0,
        sheetNames: [],
        firstSheetRows: 0,
        firstSheetCols: 0,
        estimatedSize: 'Unknown'
      };
    }
  }

  /**
   * Check if buffer contains valid XLSX data
   * @param buffer ArrayBuffer to check
   * @returns True if buffer appears to be XLSX format
   */
  static isValidXLSX(buffer: ArrayBuffer): boolean {
    try {
      // Try to read just the structure without processing all data
      const workbook = XLSX.read(buffer, { 
        type: 'array', 
        bookSheets: true, // Only read sheet names
        bookProps: true   // Only read properties
      });
      
      return workbook.SheetNames.length > 0;
    } catch (error) {
      return false;
    }
  }
}